/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useMemo } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { jsx } from '@atlaskit/css';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { JSONTransformer } from '@atlaskit/editor-json-transformer/JSONTransformer-2';
import type { JSONNode } from '@atlaskit/editor-json-transformer/types';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import { ReactRenderer } from '@atlaskit/renderer';
import { RendererActionsContext } from '@atlaskit/renderer/actions/renderer-actions-context';

import { AgentManagedExtensionView } from '../common/AgentManagedExtensionView';

/**
 * Loose `node` shape compatible with both ProseMirror `Node`s (passed by the custom nodeView) and
 * ADF JSON nodes (passed by the manifest's render function). Only `attrs` and `content` are read.
 */
interface Props {
	// Set to true when this block is the current ProseMirror NodeSelection
	isSelected?: boolean;
	node: { attrs?: Record<string, unknown>; content?: unknown };
	providerFactory?: ProviderFactory;
	/** When true, renders only the body content without interactive chrome (hover border, label). */
	readOnly?: boolean;
}

const jsonTransformer = new JSONTransformer();

/**
 * Renderer/view-only renderer for the agent-managed bodied extension. Body content is supplied
 * via `node.content` (ADF JSON or PM `Fragment`) and rendered with `<ReactRenderer>`.
 *
 * This render path intentionally does not fetch AIMO metadata. Owner, execution metadata, and
 * config hydration are configuration-popup concerns; view-only rendering must be driven by the
 * persisted body ADF.
 */
export const AgentManagedBodiedExtensionRenderer = ({
	node,
	providerFactory,
	isSelected,
	readOnly,
}: Props): React.ReactElement | null => {
	// ---------------------------------------------------------------------------
	// Body content
	// ---------------------------------------------------------------------------
	const doc: DocNode = useMemo(() => {
		let content: JSONNode[];
		if (Array.isArray(node.content)) {
			content = node.content;
		} else if (node.content instanceof Fragment) {
			// Already PM nodes — encode directly to ADF JSON
			content = [];
			node.content.forEach((child) => {
				content.push(jsonTransformer.encodeNode(child));
			});
		} else {
			content = [{ type: 'paragraph' }];
		}
		// This is already valid ADF — just wrap it as a doc
		return { version: 1, type: 'doc', content } as DocNode;
	}, [node.content]);

	return (
		<AgentManagedExtensionView isSelected={isSelected} readOnly={readOnly}>
			<RendererActionsContext>
				<ReactRenderer document={doc} appearance="full-width" dataProviders={providerFactory} />
			</RendererActionsContext>
		</AgentManagedExtensionView>
	);
};
